// Price and Organization models based on Android implementation
import { Organisation } from '../organisation/OrganisationModelV2';

export interface LocationModel {
  village?: string;
  subdistrict?: string;
}

export enum OrganizationType {
  MILL = 'mill',
  RAMP = 'ramp',
  LOADING_RAMP = 'loading_ramp',
  HANDLING = 'handling',
  DROP_OFF = 'drop_off',
  UNKNOWN = 'unknown',
}

export enum NiagaSortingStrictness {
  STRICT = 'strict',
  MODERATE = 'moderate',
  LENIENT = 'lenient',
  UNKNOWN = 'unknown',
}

export interface QualityPriceLevel {
  name: string;
  delta: number;
  price: number;
  cardinality: number;
  price_pempem: number;
}

export interface BidModel {
  id: number;
  market_day: string;
  price_per_kg: number;
  updated_by: number;
}

export interface BuyerOrganizationModel {
  organisation_id: number;
  organisation_name: string;
  bids: BidModel[];
}

export interface PriceModel {
  id: number;
  type: string;
  plotId?: number;
  groupId?: number;
  grading?: number;
  programName?: string;
  warningMessage?: string;
  spsiIncluded?: boolean;
  acceptsNeMobileDropoff?: boolean;
  pricePerUnit: number;
  programPricePerUnit?: number;
  groupPricePerUnit?: number;
  qualityPriceLevels?: QualityPriceLevel[];
  organisation: Organisation;
  distance?: number;
}

// Helper function to safely convert enum values
export const safeValueOf = <T extends Record<string, string>>(
  enumObj: T,
  value: string | null | undefined
): T[keyof T] => {
  const upperValue = value?.toUpperCase();
  const found = Object.values(enumObj).find(
    (v) => v.toUpperCase() === upperValue
  );
  return (found || enumObj.UNKNOWN) as T[keyof T];
};

// Build PriceModel from Mapbox feature properties
export const buildPriceFromFeature = (
  feature: mapboxgl.MapboxGeoJSONFeature,
  currentUserLocation: [number, number] | null
): PriceModel => {
  const props = feature.properties || {};
  const geometry = feature.geometry as GeoJSON.Point;
  const point: [number, number] = geometry.coordinates as [number, number];

  const organizationType = safeValueOf(
    OrganizationType,
    props.organisation_type
  );

  return {
    id: Number(props.id) || 0,
    type: props.price_type || '',
    plotId: props.plot_id ? Number(props.plot_id) : undefined,
    groupId: props.group_id ? Number(props.group_id) : undefined,
    grading: props.grading_ratio ? Number(props.grading_ratio) : undefined,
    programName: props.program_name || undefined,
    warningMessage: props.warning_message || undefined,
    spsiIncluded: props.spsi_included === true || props.spsi_included === 'true',
    acceptsNeMobileDropoff: props.accepts_ne_mobile_dropoff === true || props.accepts_ne_mobile_dropoff === 'true',
    pricePerUnit: Number(props.price_per_unit) || 0,
    programPricePerUnit: props.program_delta_bonus
      ? Number(props.program_delta_bonus)
      : undefined,
    groupPricePerUnit: props.group_price_per_unit
      ? Number(props.group_price_per_unit)
      : undefined,
    qualityPriceLevels: props.qbp_prices
      ? JSON.parse(props.qbp_prices)
      : undefined,
    organisation: new Organisation({
      id: Number(props.organisation_id) || 0,
      name: props.organisation_name || '',
      type: organizationType,
      mill_use_ne_mobile:
        props.mill_use_ne_mobile === true ||
        props.mill_use_ne_mobile === 'true',
      dropoff_document_required: props.spb_document || undefined,
      sorting_strictness: props.sorting_strictness,
      minimum_quality_level: props.minimum_quality_level || undefined,
      accepts_ne_mobile_dropoff:
        props.accepts_ne_mobile_dropoff === true ||
        props.accepts_ne_mobile_dropoff === 'true',
      location: {
        village: props.location_village || undefined,
        sub_district: props.location_sub_district || undefined,
      },
    }),
    distance: currentUserLocation
      ? calculateDistance(point, currentUserLocation)
      : undefined,
  };
};

// Haversine distance calculation
const calculateDistance = (
  point: [number, number],
  userLocation: [number, number]
): number => {
  const [lon1, lat1] = point;
  const [lon2, lat2] = userLocation;

  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

// Build BuyerOrganizationModel from Mapbox feature properties
export const buildBuyerFromFeature = (
  feature: mapboxgl.MapboxGeoJSONFeature
): BuyerOrganizationModel => {
  const props = feature.properties || {};
  
  return {
    organisation_id: Number(props.organisation_id) || 0,
    organisation_name: props.organisation_name || '',
    bids: [{
      id: Number(props.id) || 0,
      market_day: props.market_date || '',
      price_per_kg: Number(props.price_per_unit) || 0,
      updated_by: Number(props.publisher_id) || 0,
    }],
  };
};
